import { FC, ReactNode, useMemo, useState } from 'react'

import useInfiniteScroll from 'react-infinite-scroll-hook'
import {
  IconClock,
  IconPlus,
  Button,
  EmptyState,
  ErrorMessage,
  GreyGrey80,
  Inspector,
  Skeleton,
  Spinner,
  Tooltip,
  GreyGrey50,
  Center,
  Flex
} from '@sequencehq/core-components'
import { useSequenceInfiniteQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { SECONDS } from '@sequencehq/utils/dates'
import { ActivityLogItem } from '../ActivityLogItem'
import { ActivityLogNoteForm, ActivityLogNoteItem } from '../ActivityLogNote'
import { ActivityEntityType } from '../types'
import {
  type ActivityLogDiffFn,
  getDiffChanges
} from '../ActivityLogItem/activityLogDiff.helpers'

export const InspectorContent: FC<{
  entityId: string
  entityType: ActivityEntityType
  /** When provided, only show activity log items whose diff changes include at least one of these field names */
  filterFields?: Set<string>
  /** Override the activity log item title */
  itemTitle?: string
  /** Custom diff function for domain-specific formatting */
  diffFn?: ActivityLogDiffFn
}> = ({ entityId, entityType, filterFields, itemTitle, diffFn }) => {
  const [showNoteForm, setShowNoteForm] = useState<boolean>(false)

  const { infiniteQuery } = useSequenceInfiniteQuery(
    dashboardv99990101Client.getActivityLogs,
    { limit: 50, activityLogObjectEntityId: entityId },
    { refetchInterval: 5 * SECONDS }
  )

  const [infiniteRef] = useInfiniteScroll({
    loading: infiniteQuery.isFetching,
    hasNextPage: infiniteQuery.hasNextPage,
    onLoadMore: infiniteQuery.fetchNextPage,
    rootMargin: `0px 0px 50px 0px`
  })

  const pages = infiniteQuery.data?.pages

  const items = useMemo(() => {
    const allItems = (pages ?? []).flatMap(page => page.items) ?? []

    if (!filterFields) return allItems

    return allItems.reduce<typeof allItems>((acc, activityLog) => {
      if (activityLog.activityType === 'NOTE') return [...acc, activityLog]

      const filteredLogItems = activityLog.activityLogItems.filter(item => {
        const changes = getDiffChanges(item.metadata)
        return changes.some(change => filterFields.has(change.field))
      })

      if (filteredLogItems.length === 0) return acc

      return [...acc, { ...activityLog, activityLogItems: filteredLogItems }]
    }, [])
  }, [pages, filterFields])

  if (infiniteQuery.isError) return <ErrorState />
  if (infiniteQuery.isLoading) return <LoadingState />

  return (
    <Wrapper
      titleAction={<ButtonAddNote onClick={() => setShowNoteForm(true)} />}
    >
      <Flex
        direction="column"
        gap="0px"
        width="100%"
        height="100%"
        align="center"
      >
        {showNoteForm && (
          <ActivityLogNoteForm
            entityId={entityId}
            entityType={entityType}
            isLast={items.length === 0}
            onDismiss={() => setShowNoteForm(false)}
          />
        )}

        {items.length === 0 && <ListEmptyState isVisible={!showNoteForm} />}

        {items.map((activityLog, logIndex) => {
          return activityLog.activityLogItems.map(
            (activityLogItem, logItemIndex) => {
              const isLast: boolean =
                logIndex === items.length - 1 &&
                logItemIndex === activityLog.activityLogItems.length - 1

              const dataTestId = `activity-log.id.${activityLogItem.id}`

              if (activityLog.activityType === 'NOTE') {
                return (
                  <ActivityLogNoteItem
                    key={activityLogItem.id}
                    data-testid={dataTestId}
                    summary={activityLogItem.summary}
                    email={activityLogItem.activityLogActor.email}
                    createdAt={activityLog.createdAt}
                    activityLogId={activityLog.id}
                    isLast={isLast}
                  />
                )
              }

              return (
                <ActivityLogItem
                  key={activityLogItem.id}
                  data-testid={dataTestId}
                  name={activityLogItem.name}
                  email={activityLogItem.activityLogActor.email}
                  createdAt={activityLog.createdAt}
                  activityType={activityLog.activityType}
                  entityType={activityLogItem.activityLogObject.entityType}
                  isLast={isLast}
                  metadata={activityLogItem.metadata}
                  filterFields={filterFields}
                  itemTitle={itemTitle}
                  diffFn={diffFn}
                />
              )
            }
          )
        })}
        {infiniteQuery.hasNextPage && (
          <Center ref={infiniteRef}>
            <Spinner height="16px" width="16px" color={GreyGrey50} />
          </Center>
        )}
      </Flex>
    </Wrapper>
  )
}

const ButtonAddNote: FC<{ onClick: () => void }> = ({ onClick }) => {
  const label = 'Add note'
  return (
    <Tooltip label={label}>
      <Button
        variant="ghost"
        icon={<IconPlus color={GreyGrey80} />}
        aria-label={label}
        onClick={onClick}
        size="24px"
        data-testid="activity-log.inspector.header.buttonAddNote"
      />
    </Tooltip>
  )
}

const Wrapper: FC<{
  children: ReactNode
  hideTitle?: boolean
  titleAction?: ReactNode
}> = ({ children, hideTitle = false, titleAction }) => (
  <Inspector.Content
    title={hideTitle ? undefined : 'Activity log'}
    titleAction={titleAction}
    data-testid="activity-log.inspector.content"
  >
    {children}
  </Inspector.Content>
)

const LoadingState: FC = () => (
  <Wrapper>
    <Flex
      direction="column"
      width="100%"
      height="100%"
      align="center"
      gap="8px"
    >
      {Array.from({ length: 15 }).map((_, key) => (
        <Skeleton key={key} />
      ))}
    </Flex>
  </Wrapper>
)

const ErrorState: FC = () => (
  <Wrapper>
    <Center width="100%" height="100%" textAlign="center">
      <ErrorMessage data-testid="activity-log.inspector.content.errorMessage" />
    </Center>
  </Wrapper>
)

const ListEmptyState: FC<{ isVisible: boolean }> = ({ isVisible }) => (
  <Center height="100%" visibility={isVisible ? 'visible' : 'hidden'}>
    <EmptyState
      icon={IconClock}
      iconSize="sm"
      title="No recent activity"
      data-testid="activity-log.inspector.content.emptyState"
    />
  </Center>
)
